import { Box, Grid } from '@mui/material';
import { searchOrCreateBusiness } from 'lib/api/business';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AuthState, User } from 'services/redux/authSlice';

import Blacklist from '../Blacklist';
import Editor from './components/Editor';
import Executives from './components/Executives';
import Observations from './components/Observations';
import Summary from './components/Summary';
import Tags from './components/Tags';

interface BusinessSummary {
  id: number;
  businessIdentifier: string;
  companyId: number;
  email: string | null;
  activityStartDate: string | null;
  region: string | null;
  province: string | null;
  commune: string | null;
  address: string | null;
  economicSector: string | null;
  economicSubSector: string | null;
  economicActivity: string | null;
  salesSegment: string | null;
  observations: string | null;
}

interface ClientProps {
  businessId?: string | null;
  businessName?: string | null;
  showEnrolledPath?: boolean;
}

const Client = (props: ClientProps) => {
  const { businessId, businessName, showEnrolledPath } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [enrolledPath, setEnrolledPath] = useState<string>('');
  const [businessSummary, setBusinessSummary] = useState<BusinessSummary>({
    id: 0,
    businessIdentifier: '',
    companyId: 0,
    email: '',
    activityStartDate: null,
    region: '',
    province: '',
    commune: '',
    address: '',
    economicSector: '',
    economicSubSector: '',
    economicActivity: '',
    salesSegment: '',
    observations: '',
  });

  const { id } = useParams<{
    id: string;
    businessId: string;
    page: string;
  }>();

  const setUp = async () => {
    try {
      setLoading(true);
      setEditing(false);
      const identifier = businessId || id;
      if (!identifier) {
        showAlert(dispatch, 'error', 'No se ha encontrado el id del cliente');
        return;
      }
      const response = await searchOrCreateBusiness(dispatch, identifier);
      setBusinessSummary(response.summary);
      setEnrolledPath(response.enrolledPath);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void setUp();
  }, []);

  if (loading) return <CenteredCircularProgress relative />;

  return (
    <Box>
      {!editing && (
        <Summary
          setEditing={setEditing}
          user={user}
          businessIdentifier={businessSummary.businessIdentifier}
          email={businessSummary.email}
          activityStartDate={businessSummary.activityStartDate}
          region={businessSummary.region}
          province={businessSummary.province}
          commune={businessSummary.commune}
          address={businessSummary.address}
          economicSector={businessSummary.economicSector}
          economicSubSector={businessSummary.economicSubSector}
          economicActivity={businessSummary.economicActivity}
          salesSegment={businessSummary.salesSegment}
          enrolledPath={showEnrolledPath ? enrolledPath : null}
        />
      )}
      {editing && (
        <Editor
          setEditing={setEditing}
          user={user as User}
          setUp={() => setUp()}
          businessIdentifier={businessSummary.businessIdentifier}
          email={businessSummary.email}
          region={businessSummary.region}
          province={businessSummary.province}
          commune={businessSummary.commune}
          address={businessSummary.address}
          economicSector={businessSummary.economicSector}
          economicSubSector={businessSummary.economicSubSector}
          economicActivity={businessSummary.economicActivity}
          salesSegment={businessSummary.salesSegment}
        />
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Tags businessId={businessSummary.businessIdentifier} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Blacklist
            businessId={businessSummary.businessIdentifier}
            businessName={businessName ?? ''}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Observations
            user={user}
            observations={businessSummary.observations ?? ''}
            businessIdentifier={businessSummary.businessIdentifier ?? ''}
            setUp={() => void setUp()}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Executives businessIdentifier={businessSummary.businessIdentifier} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Client;
